import { useEffect, useRef } from 'react';
import maplibregl from 'maplibre-gl';
import { CarFront } from 'lucide-react';
import { renderToStaticMarkup } from 'react-dom/server';
import { interpolateAlongPath } from '../../lib/geoInterpolate.js';

interface VehicleMarkerProps {
  map: maplibregl.Map;
  routeGeometry: GeoJSON.LineString;
  progress: number;
  visible: boolean;
}

export default function VehicleMarker({ map, routeGeometry, progress, visible }: VehicleMarkerProps) {
  const markerRef = useRef<maplibregl.Marker | null>(null);

  useEffect(() => {
    if (!visible) {
      if (markerRef.current) {
        markerRef.current.remove();
        markerRef.current = null;
      }
      return;
    }

    const coords = routeGeometry.coordinates as [number, number][];
    if (!coords.length) return;

    const { lng, lat, bearing } = interpolateAlongPath(coords, progress);

    if (!markerRef.current) {
      const iconHTML = renderToStaticMarkup(
        <div className="vehicle-marker" style={{ transform: `rotate(${bearing}deg)` }}>
          <CarFront size={18} />
        </div>,
      );
      const el = document.createElement('div');
      el.innerHTML = iconHTML;
      const markerEl = el.firstElementChild as HTMLElement;

      markerRef.current = new maplibregl.Marker({ element: markerEl, anchor: 'center' })
        .setLngLat([lng, lat])
        .addTo(map);
    } else {
      const el = markerRef.current.getElement() as HTMLElement;
      const inner = el.querySelector('.vehicle-marker') as HTMLElement;
      if (inner) {
        inner.style.transform = `rotate(${bearing}deg)`;
      }
      markerRef.current.setLngLat([lng, lat]);
    }

    return () => {
      if (markerRef.current) {
        markerRef.current.remove();
        markerRef.current = null;
      }
    };
  }, [map, routeGeometry, progress, visible]);

  return null;
}
